import { Download, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Kpi, SortableHead } from "./subscriberRoster/RosterTableParts";
import {
  exportRosterCsv,
  useSubscriberRoster,
} from "./subscriberRoster/useSubscriberRoster";
import { STATUS_TONE, TIER_LABEL, fmtDate, fmtUsd } from "./subscriberRoster/types";

export const SubscriberRosterCard = ({
  environment,
}: {
  environment: "sandbox" | "live";
}) => {
  const r = useSubscriberRoster(environment);
  const {
    data, loading, search, setSearch, tierFilter, setTierFilter,
    statusFilter, setStatusFilter, cycleFilter, setCycleFilter,
    sortKey, sortDir, toggleSort, page, setPage, totalPages,
    rows, sorted, pageRows, kpis, load,
  } = r;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Subscriber roster</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            One row per Stripe subscription in the {environment === "live" ? "Live" : "Test"} environment. MRR is derived from the price's lookup key (yearly ÷ 12). Internal tiers don't contribute to MRR.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => exportRosterCsv(sorted, environment)}
            disabled={!sorted.length}
          >
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
          <Kpi label="Active" value={kpis.active.toString()} />
          <Kpi label="Trialing" value={kpis.trialing.toString()} />
          <Kpi label="Past due" value={kpis.pastDue.toString()} tone={kpis.pastDue > 0 ? "destructive" : undefined} />
          <Kpi label="Canceling at period end" value={kpis.cancelingAtEnd.toString()} />
          <Kpi label="MRR" value={fmtUsd(kpis.mrr)} emphasis />
          <Kpi label="ARR" value={fmtUsd(kpis.arr)} emphasis />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search email or username"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 w-64"
            />
          </div>
          <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); setPage(1); }}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Tier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tiers</SelectItem>
              {["starter", "plus", "pro", "elite", "agency", "comp", "admin_workspace"].map((t) => (
                <SelectItem key={t} value={t}>{TIER_LABEL[t] ?? t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {["active", "trialing", "past_due", "canceled", "incomplete", "incomplete_expired", "unpaid", "paused"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={cycleFilter} onValueChange={(v) => { setCycleFilter(v); setPage(1); }}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Cycle" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cycles</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground ml-auto">
            {sorted.length} of {rows.length} rows
          </span>
        </div>

        {loading && !data ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Email" k="email" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <TableHead>Username</TableHead>
                  <SortableHead label="Tier" k="tier" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHead label="Cycle" k="cycle" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHead label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHead label="Renews / ends" k="current_period_end" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHead label="MRR" k="mrr_cents" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} className="text-right" />
                  <SortableHead label="Joined" k="joined_at" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                      No subscriptions match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((row) => {
                    const internal = row.account_tier && ["comp", "admin_workspace"].includes(row.account_tier);
                    return (
                      <TableRow key={row.stripe_subscription_id} className={internal ? "opacity-70" : undefined}>
                        <TableCell className="font-mono text-xs">{row.email ?? "—"}</TableCell>
                        <TableCell className="text-xs">{row.username ?? row.display_name ?? "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{row.account_tier ? TIER_LABEL[row.account_tier] ?? row.account_tier : "—"}</Badge>
                        </TableCell>
                        <TableCell className="capitalize text-xs">{row.cycle}</TableCell>
                        <TableCell>
                          <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status] ?? "bg-muted text-muted-foreground"}`}>
                            {row.status}
                          </span>
                          {row.cancel_at_period_end && (
                            <span className="ml-2 text-xs text-muted-foreground">cancels at period end</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{fmtDate(row.current_period_end)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{row.mrr_cents > 0 ? fmtUsd(row.mrr_cents) : "—"}</TableCell>
                        <TableCell className="text-xs">{fmtDate(row.joined_at)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
